import { Label } from "layout/typography/Label";
import { sortBy } from "lodash";
import { buildPayerSwitchMessage } from "pages/CreateOrder/components/PatientBenefitsSection/eligibilityMessages";
import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { useState } from "react";
import { useGetAllAccountsQuery } from "store/queries/account";
import { useEvaluatePayerAutoSelectionMutation } from "store/queries/payerAutoSelection";
import { useGetPayersByAccountQuery } from "store/queries/payers";
import styled from "styled-components";
import { IPayerAutoSelectionEvaluateResult } from "types/PayerAutoSelection/PayerAutoSelection";

import { REASON_LABELS } from "./payerAutoSelectionWording";

const Banner = styled.div`
  padding: 0.75rem 0.875rem;
  border: 1px solid #f0c36d;
  background: #fff8e6;
  border-radius: 0.5rem;
  color: #7a4d00;
  font-size: 0.875rem;
  line-height: 1.45;
`;

const listOf = (data: any): any[] => (data as any)?.data || data || [];

interface IPayerAutoSelectionTesterProps {
  visible: boolean;
  onHide: () => void;
}

/** The messages box, one message per non-blank line; undefined when there are none. The API takes at most 50. */
const messageLines = (text: string): string[] | undefined => {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 50);
  return lines.length > 0 ? lines : undefined;
};

/**
 * CLE-2845 — "Test a response", a dialog opened from the tab's button row: runs the SAVED rules, in
 * their saved order, through the same code path as a real check, on a response typed in here.
 * Nothing is sent to pVerify and nothing is stored. What was typed stays while the tab is open, so
 * closing and reopening the dialog does not lose it.
 */
export function PayerAutoSelectionTester({
  visible,
  onHide,
}: IPayerAutoSelectionTesterProps) {
  const [accountId, setAccountId] = useState<string | null>(null);
  const [payerId, setPayerId] = useState<number | null>(null);
  const [planName, setPlanName] = useState("");
  const [payerName, setPayerName] = useState("");
  const [planNumber, setPlanNumber] = useState("");
  const [policyType, setPolicyType] = useState("");
  // CLE-3004 — the other places pVerify says Medicaid: the plan network (SNP-DE) and the benefit messages.
  const [planNetworkName, setPlanNetworkName] = useState("");
  const [messagesText, setMessagesText] = useState("");
  const [result, setResult] =
    useState<IPayerAutoSelectionEvaluateResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: accountsData } = useGetAllAccountsQuery(null, {
    skip: !visible,
  });
  const { data: payersData } = useGetPayersByAccountQuery(accountId || "", {
    skip: !visible || !accountId,
  });
  const [evaluate, { isLoading }] = useEvaluatePayerAutoSelectionMutation();

  const accounts = listOf(accountsData);
  const payers = listOf(payersData);
  const accountOptions = sortBy(
    accounts.map((account: any) => ({
      label: account.name,
      value: account.id,
    })),
    ["label"]
  );
  const payerOptions = sortBy(
    payers.map((payer: any) => ({ label: payer.name, value: payer.id })),
    ["label"]
  );
  const selectedPayerName =
    payers.find((payer: any) => payer.id === payerId)?.name ?? "";

  const run = async () => {
    if (!accountId || payerId === null) return;
    setError(null);
    try {
      const response = await evaluate({
        accountId,
        payerId,
        planName: planName || undefined,
        payerName: payerName || undefined,
        planNumber: planNumber || undefined,
        policyType: policyType || undefined,
        planNetworkName: planNetworkName || undefined,
        messages: messageLines(messagesText),
      }).unwrap();
      setResult(response);
    } catch (caught: any) {
      setResult(null);
      setError(
        caught?.data?.message || caught?.message || "The test could not run."
      );
    }
  };

  const recommendation = result?.recommendation ?? null;

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Test a response"
      style={{ width: "min(760px, 95vw)" }}
      data-testid="payerAutoSelection.testerDialog"
    >
      {/* The dialog renders through a portal, outside the tab's Surface, so it carries its own. */}
      <Surface data-testid="payerAutoSelection.tester">
        <p className="mt-0 mb-3 text-color-secondary">
          Enter what pVerify returned for a check. The result uses the rules as
          saved, in their order, through the same code as a real eligibility
          check.
        </p>

        <div className="grid">
          <div className="col-12 md:col-6 flex flex-column gap-1">
            <Label htmlFor="payerAutoSelection.tester.account">Facility</Label>
            <Dropdown
              inputId="payerAutoSelection.tester.account"
              options={accountOptions}
              value={accountId}
              onChange={(event) => {
                setAccountId(event.value ?? null);
                setPayerId(null);
              }}
              placeholder="Choose a facility"
              filter
            />
          </div>
          <div className="col-12 md:col-6 flex flex-column gap-1">
            <Label htmlFor="payerAutoSelection.tester.payer">
              Payer staff selected
            </Label>
            <Dropdown
              inputId="payerAutoSelection.tester.payer"
              options={payerOptions}
              value={payerId}
              onChange={(event) => setPayerId(event.value ?? null)}
              placeholder="Choose a payer"
              disabled={!accountId}
              filter
            />
          </div>
          <div className="col-12 flex flex-column gap-1">
            <Label htmlFor="payerAutoSelection.tester.planName">
              Plan name pVerify returned
            </Label>
            <InputTextarea
              id="payerAutoSelection.tester.planName"
              rows={2}
              value={planName}
              onChange={(event) => setPlanName(event.target.value)}
            />
          </div>
          <div className="col-12 md:col-4 flex flex-column gap-1">
            <Label htmlFor="payerAutoSelection.tester.payerName">
              pVerify payer name
            </Label>
            <InputText
              id="payerAutoSelection.tester.payerName"
              value={payerName}
              onChange={(event) => setPayerName(event.target.value)}
            />
          </div>
          <div className="col-12 md:col-4 flex flex-column gap-1">
            <Label htmlFor="payerAutoSelection.tester.planNumber">
              PlanNumber
            </Label>
            <InputText
              id="payerAutoSelection.tester.planNumber"
              value={planNumber}
              onChange={(event) => setPlanNumber(event.target.value)}
            />
          </div>
          <div className="col-12 md:col-4 flex flex-column gap-1">
            <Label htmlFor="payerAutoSelection.tester.policyType">
              PolicyType
            </Label>
            <InputText
              id="payerAutoSelection.tester.policyType"
              value={policyType}
              onChange={(event) => setPolicyType(event.target.value)}
            />
          </div>
          <div className="col-12 flex flex-column gap-1">
            <Label htmlFor="payerAutoSelection.tester.planNetworkName">
              PlanNetworkName
            </Label>
            <InputText
              id="payerAutoSelection.tester.planNetworkName"
              value={planNetworkName}
              onChange={(event) => setPlanNetworkName(event.target.value)}
            />
          </div>
          <div className="col-12 flex flex-column gap-1">
            <Label htmlFor="payerAutoSelection.tester.messages">
              Benefit messages (one per line)
            </Label>
            <InputTextarea
              id="payerAutoSelection.tester.messages"
              rows={3}
              value={messagesText}
              onChange={(event) => setMessagesText(event.target.value)}
            />
          </div>
        </div>

        <Button
          type="button"
          label="Test"
          icon="pi pi-play"
          className="p-button-sm mb-3"
          loading={isLoading}
          disabled={!accountId || payerId === null}
          onClick={run}
          data-testid="payerAutoSelection.tester.run"
        />

        {error && <Message severity="error" text={error} />}

        {result && (
          <div
            className="flex flex-column gap-2"
            data-testid="payerAutoSelection.tester.result"
          >
            {recommendation ? (
              <>
                <p className="m-0 font-semibold">
                  Switch: {selectedPayerName} → {recommendation.name}
                </p>
                <p className="m-0 text-sm text-color-secondary">
                  Rule: {REASON_LABELS[recommendation.reason]}
                  {recommendation.matchedText
                    ? `: ${recommendation.matchedText}`
                    : ""}
                </p>
                <Banner data-testid="payerAutoSelection.tester.banner">
                  <small className="block font-semibold mb-1">
                    WHAT STAFF SEE ON THE ESTIMATE
                  </small>
                  {buildPayerSwitchMessage(
                    recommendation,
                    selectedPayerName,
                    planName
                  )}
                </Banner>
              </>
            ) : (
              <p className="m-0 font-semibold">
                No change — stays on {selectedPayerName}
              </p>
            )}
            <ul
              className="m-0 pl-3 text-sm text-color-secondary"
              data-testid="payerAutoSelection.tester.trace"
            >
              {result.trace.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        )}
      </Surface>
    </Dialog>
  );
}
